import React from 'react'
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Select,
  Switch,
  Typography
} from 'antd'
import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { sellerApi } from 'api/seller'
import {
  IInsalesFieldStat,
  IInsalesProbeResponse,
  IInsalesSettingsResponse,
  IInsalesSyncResponse,
  IUpdateInsalesSettingsRequest,
  TInsalesConnectionType,
  TVariantSeller
} from 'models/seller'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { StockConfirmationStatus } from 'components/sellers/StockConfirmationStatus/StockConfirmationStatus'
import { SectionTitle } from 'components/ui'
import styles from './SellerApiInsales.module.scss'

interface ISellerApiInsalesProps {
  type: TVariantSeller
  sellerId: string
}

const SHOP_URL_PLACEHOLDER = 'https://market.example.ru'

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'нет данных'
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))
}

const formatNumber = (value: number | null) =>
  value === null ? '—' : new Intl.NumberFormat('ru-RU').format(value)

// Названий складов и типов цен InSales не отдаёт, поэтому показываем ключ как есть
const getStockLabel = (key: string) => {
  const warehouseMatch = key.match(/^quantity_at_warehouse(\d+)$/)
  return warehouseMatch ? `склад${warehouseMatch[1]}` : key
}

// Статистику показываем прямо в опции: по одному ключу склад не узнать,
// продавец опознаёт нужное поле по его числам
const buildOptions = (
  keys: string[],
  stats: IInsalesFieldStat[],
  getLabel: (key: string) => string
) =>
  keys.map((key) => {
    const stat = stats.find((item) => item.key === key)
    const hint = stat
      ? `заполнено ${formatNumber(stat.filled)}, сумма ${formatNumber(
          stat.sum
        )}, пример ${stat.sample ?? '—'}`
      : null

    return {
      value: key,
      label: hint ? `${getLabel(key)} — ${hint}` : getLabel(key)
    }
  })

export const SellerApiInsales: React.FC<ISellerApiInsalesProps> = ({
  type,
  sellerId
}) => {
  const requestSellerId = type === 'seller' ? sellerId : undefined

  const [isLoaded, setIsLoaded] = React.useState(false)
  const [isProbing, setIsProbing] = React.useState(false)
  const [isSavingConnectionType, setIsSavingConnectionType] =
    React.useState(false)
  const [isSavingPrice, setIsSavingPrice] = React.useState(false)
  const [isSavingStock, setIsSavingStock] = React.useState(false)
  const [isSavingCreateNewProducts, setIsSavingCreateNewProducts] =
    React.useState(false)
  const [isSavingFillDescription, setIsSavingFillDescription] =
    React.useState(false)
  const [isSyncing, setIsSyncing] = React.useState(false)

  const [settings, setSettings] =
    React.useState<IInsalesSettingsResponse | null>(null)
  const [probe, setProbe] = React.useState<IInsalesProbeResponse | null>(null)
  const [syncResult, setSyncResult] =
    React.useState<IInsalesSyncResponse | null>(null)
  const [shopUrl, setShopUrl] = React.useState('')
  const [apiKey, setApiKey] = React.useState('')
  const [apiPassword, setApiPassword] = React.useState('')

  const connectionType: TInsalesConnectionType =
    settings?.connection_type ?? 'SHOWCASE'
  const isApiConnection = connectionType === 'API'

  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await sellerApi.getInsalesSettings(requestSellerId)
        setSettings(response.data)
        setShopUrl(response.data.shop_url ?? '')
        setApiKey(response.data.api_key ?? '')
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoaded(true)
      }
    }

    loadSettings()
  }, [requestSellerId])

  const saveSettings = async (
    data: IUpdateInsalesSettingsRequest,
    setLoading: (value: boolean) => void,
    successMessage: string
  ) => {
    try {
      setLoading(true)
      const response = await sellerApi.updateInsalesSettings(
        data,
        requestSellerId
      )
      setSettings(response.data)
      showMessage('success', successMessage, '')
    } catch (e) {
      showServerError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleProbe = async () => {
    try {
      setIsProbing(true)
      const response = await sellerApi.probeInsales(
        {
          shop_url: shopUrl,
          connection_type: connectionType,
          ...(isApiConnection && {
            api_key: apiKey,
            api_password: apiPassword
          })
        },
        requestSellerId
      )
      setProbe(response.data)
      setShopUrl(response.data.shop_url)
      setApiPassword('')
      setSettings((prev) =>
        prev
          ? {
              ...prev,
              shop_url: response.data.shop_url,
              api_key: isApiConnection ? apiKey : prev.api_key,
              has_api_password: isApiConnection
                ? prev.has_api_password || Boolean(apiPassword)
                : prev.has_api_password,
              price_fields: response.data.price_fields.map((item) => item.key),
              stock_fields: response.data.stock_fields.map((item) => item.key)
            }
          : prev
      )
      showMessage(
        'success',
        'Магазин прочитан',
        `Товаров в магазине: ${response.data.products_total}, совпало по артикулу: ${response.data.matched}`
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsProbing(false)
    }
  }

  const handleConnectionTypeChange = (type: TInsalesConnectionType) => {
    if (type === connectionType) return

    saveSettings(
      { connection_type: type },
      setIsSavingConnectionType,
      'Способ подключения сохранён'
    )
  }

  const handleSelectPriceField = (value: string) =>
    saveSettings({ price_field: value }, setIsSavingPrice, 'Поле цены сохранено')

  const handleSelectStockField = (value: string) =>
    saveSettings(
      { stock_field: value },
      setIsSavingStock,
      'Склад остатков сохранён'
    )

  const handleCreateNewProductsChange = (checked: boolean) =>
    saveSettings(
      { create_new_products: checked },
      setIsSavingCreateNewProducts,
      'Настройки InSales сохранены'
    )

  const handleFillDescriptionChange = (checked: boolean) =>
    saveSettings(
      { fill_description_on_create: checked },
      setIsSavingFillDescription,
      'Настройки InSales сохранены'
    )

  const handleSync = async () => {
    try {
      setIsSyncing(true)
      const response = await sellerApi.syncInsales(requestSellerId)
      setSyncResult(response.data)

      const settingsResponse = await sellerApi.getInsalesSettings(
        requestSellerId
      )
      setSettings(settingsResponse.data)

      showMessage(
        'success',
        'Синхронизация завершена',
        `Обновлено цен: ${response.data.price_updated}, остатков: ${response.data.stock_updated}, создано товаров: ${response.data.created}`
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSyncing(false)
    }
  }

  if (!isLoaded) {
    return null
  }

  const priceOptions = buildOptions(
    settings?.price_fields ?? [],
    probe?.price_fields ?? [],
    (key) => key
  )
  const stockOptions = buildOptions(
    settings?.stock_fields ?? [],
    probe?.stock_fields ?? [],
    getStockLabel
  )

  const canProbe = Boolean(
    shopUrl.trim() &&
      (!isApiConnection ||
        (apiKey.trim() && (apiPassword.trim() || settings?.has_api_password)))
  )
  const canSync = Boolean(
    settings?.shop_url && settings?.price_field && settings?.stock_field
  )

  return (
    <div className={styles.root}>
      <SectionTitle level={2} className={styles.title}>
        InSales Синхронизация
      </SectionTitle>
      <StockConfirmationStatus sellerId={sellerId} />
      <Alert
        type='info'
        showIcon
        icon={<InfoCircleOutlined />}
        message='Два способа синхронизации'
        description={
          <div className={styles.hint}>
            <p>
              <b>По API</b> — нужен ключ доступа, он может быть платным. Зато у
              товара приходит полное описание.
            </p>
            <p>
              <b>По ссылке на витрину</b> — бесплатно и без ключей, достаточно
              адреса магазина. Описание приходит только краткое.
            </p>
            <p>
              Цены, остатки и фотографии приходят одинаково. Порядок в обоих
              случаях: адрес магазина → «Проверить» → поле цены и склад →
              «Синхронизировать сейчас».
            </p>
          </div>
        }
        className={styles.alert}
      />

      <div className={`${styles.switchRow} ${styles.connectionRow}`}>
        <div className={styles.connectionOption}>
          <Switch
            checked={isApiConnection}
            loading={isSavingConnectionType}
            onChange={() => handleConnectionTypeChange('API')}
          />
          <Typography.Text className={styles.switchLabel}>
            По API
          </Typography.Text>
        </div>
        <div className={styles.connectionOption}>
          <Switch
            checked={!isApiConnection}
            loading={isSavingConnectionType}
            onChange={() => handleConnectionTypeChange('SHOWCASE')}
          />
          <Typography.Text className={styles.switchLabel}>
            По ссылке
          </Typography.Text>
        </div>
      </div>

      {!isApiConnection && (
        <Alert
          type='info'
          showIcon
          icon={<InfoCircleOutlined />}
          message={
            <>
              Впишите адрес магазина и нажмите «Проверить», ключи не нужны.
              Читаем публичный JSON категории all, он «Позволяет динамически
              получить данные о товарах по заданным фильтрам, с постраничной
              навигацией». Проверить можно и в браузере: адрес вида{' '}
              <b>магазин.ru/collection/all.json</b> должен открываться.
            </>
          }
          className={styles.alert}
        />
      )}

      {isApiConnection && (
        <Alert
          type='info'
          showIcon
          icon={<InfoCircleOutlined />}
          message='Ключ и пароль создаются в InSales: «Для создания ключа доступа перейдите в панели администратора в раздел Расширения → Разработчикам и нажмите "Создать новый ключ доступа"». Адрес магазина — технический или обычный домен, его можно взять «из строки "Пример URL" около выданного ключа доступа». Логин-пароль от панели администратора не подойдёт: «иначе ваш аккаунт может быть заблокирован».'
          className={styles.alert}
        />
      )}

      <div className={styles.urlRow}>
        <Input
          value={shopUrl}
          placeholder={SHOP_URL_PLACEHOLDER}
          onChange={(event) => setShopUrl(event.target.value)}
          className={styles.urlInput}
        />
        <Button
          type='primary'
          loading={isProbing}
          disabled={!canProbe}
          onClick={handleProbe}
        >
          Проверить
        </Button>
      </div>

      {isApiConnection && (
        <div className={styles.credentialsRow}>
          <Input
            value={apiKey}
            placeholder='Ключ доступа'
            onChange={(event) => setApiKey(event.target.value)}
          />
          <Input.Password
            value={apiPassword}
            placeholder={
              settings?.has_api_password ? 'Пароль сохранён' : 'Пароль доступа'
            }
            onChange={(event) => setApiPassword(event.target.value)}
          />
        </div>
      )}

      {probe && (
        <Descriptions
          bordered
          size='small'
          column={1}
          className={styles.probeSummary}
        >
          <Descriptions.Item label='Товаров в магазине'>
            {formatNumber(probe.products_total)}
          </Descriptions.Item>
          <Descriptions.Item label='Совпало по артикулу'>
            {formatNumber(probe.matched)}
          </Descriptions.Item>
          <Descriptions.Item label='Только в магазине'>
            {formatNumber(probe.only_in_shop)}
          </Descriptions.Item>
          <Descriptions.Item label='Только на площадке'>
            {formatNumber(probe.only_on_platform)}
          </Descriptions.Item>
        </Descriptions>
      )}

      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <Typography.Text className={styles.fieldLabel}>
            Поле цены
          </Typography.Text>
          <Select
            className={styles.select}
            placeholder='Выберите поле цены'
            options={priceOptions}
            value={settings?.price_field ?? undefined}
            loading={isSavingPrice}
            disabled={!priceOptions.length}
            onChange={handleSelectPriceField}
          />
        </div>
        <div className={styles.field}>
          <Typography.Text className={styles.fieldLabel}>
            Склад остатков
          </Typography.Text>
          <Select
            className={styles.select}
            placeholder='Выберите склад остатков'
            options={stockOptions}
            value={settings?.stock_field ?? undefined}
            loading={isSavingStock}
            disabled={!stockOptions.length}
            onChange={handleSelectStockField}
          />
        </div>
      </div>

      {!priceOptions.length && (
        <Alert
          type='warning'
          showIcon
          message='Сначала нажмите «Проверить» — поля цены и склада читаются из магазина.'
          className={styles.alert}
        />
      )}

      <div className={styles.switchGroup}>
        <div className={styles.switchRow}>
          <Switch
            checked={Boolean(settings?.create_new_products)}
            loading={isSavingCreateNewProducts}
            onChange={handleCreateNewProductsChange}
          />
          <Typography.Text className={styles.switchLabel}>
            Создавать новые карточки товаров
          </Typography.Text>
        </div>
        <div className={styles.switchRow}>
          <Switch
            checked={settings?.fill_description_on_create !== false}
            disabled={!settings?.create_new_products}
            loading={isSavingFillDescription}
            onChange={handleFillDescriptionChange}
          />
          <Typography.Text className={styles.switchLabel}>
            Заполнять описание при создании
          </Typography.Text>
        </div>
      </div>

      {settings?.create_new_products && (
        <Alert
          type='info'
          showIcon
          icon={<InfoCircleOutlined />}
          message='Категория и подкатегория новой карточки подбираются по названию товара. Если подобрать не удалось или не хватает данных, карточка создаётся черновиком.'
          className={styles.alert}
        />
      )}

      <div className={styles.syncRow}>
        <Button
          type='primary'
          icon={<SyncOutlined />}
          loading={isSyncing}
          disabled={!canSync}
          onClick={handleSync}
        >
          Синхронизировать сейчас
        </Button>
        <Typography.Text type='secondary'>
          Последняя синхронизация: {formatDate(settings?.last_sync_at)}
        </Typography.Text>
      </div>

      {settings?.last_sync_error && (
        <Alert
          type='error'
          showIcon
          message={`Ошибка синхронизации (${formatDate(
            settings.last_sync_error_at
          )})`}
          description={settings.last_sync_error}
          className={styles.alert}
        />
      )}

      {syncResult && (
        <Descriptions
          bordered
          size='small'
          column={1}
          className={styles.probeSummary}
        >
          <Descriptions.Item label='Сопоставлено товаров'>
            {formatNumber(syncResult.matched)}
          </Descriptions.Item>
          <Descriptions.Item label='Обновлено цен'>
            {formatNumber(syncResult.price_updated)}
          </Descriptions.Item>
          <Descriptions.Item label='Обновлено остатков'>
            {formatNumber(syncResult.stock_updated)}
          </Descriptions.Item>
          <Descriptions.Item label='Без изменений'>
            {formatNumber(syncResult.unchanged)}
          </Descriptions.Item>
          <Descriptions.Item label='Создано товаров'>
            {formatNumber(syncResult.created)}
          </Descriptions.Item>
          <Descriptions.Item label='Из них черновиками'>
            {formatNumber(syncResult.created_draft)}
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  )
}
